import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { getApiUrl } from '../utils/api';
import { useProject } from '../contexts/ProjectContext';
import { ReactComponent as UploadArrowIcon } from "../assets/upload-arrow.svg";
import AssetManagement, { AssetInfoBase, ResetFileInput } from './AssetManagement';
import { isProjectOwnedAssetUrl } from '../utils/assetOwnership';

export interface FontInfo extends AssetInfoBase {
  fileName?: string;
}

interface FontManagementProps {
  onFontListChange: (fonts: FontInfo[]) => void;
  initialFonts?: FontInfo[];
  onFontUploaded?: (fontName: string, serverUrl: string) => void;
  onFontDeleted?: (fontName: string) => void;
}

const FontManagement: React.FC<FontManagementProps> = ({ onFontListChange, initialFonts = [], onFontUploaded, onFontDeleted }) => {
  const { selectedProject } = useProject();
  const selectedProjectId: string | undefined = selectedProject?.id;
  const [fonts, setFonts] = useState<FontInfo[]>(initialFonts);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLicenseDialogOpen, setIsLicenseDialogOpen] = useState<boolean>(false);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);

  const pendingResetRef = useRef<ResetFileInput | null>(null);

  // Keep internal state in sync if parent updates initialFonts after mount
  useEffect(() => {
    setFonts(initialFonts);
  }, [initialFonts]);

  const uploadFontToAPI = async (file: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${getApiUrl()}/upload-font?projectId=${selectedProject!.id}`,
        { method: "POST", body: formData }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();

      if (result.status === "OK") {
        setError(null);
        console.log("Font uploaded and loaded:", file.name);
        // Build server font URL as: <server-origin>/users/<projectId>/fonts/<filename>
        const projectId: string = selectedProject!.id;
        const filename: string = encodeURIComponent(file.name);
        const serverFontUrl = `${window.location.origin}/users/${projectId}/fonts/${filename}`;
        console.log("Server font URL:", serverFontUrl);
        if (onFontUploaded) {
          onFontUploaded(file.name.replace(/\.[^/.]+$/, ''), serverFontUrl);
        }
      } else {
        setError(result.message || "Unknown error occurred");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload font");
    } finally {
      setLoading(false);
    }
  };

  const processFontFiles = (files: File[]): void => {
    const existingNamesLower: Set<string> = new Set(fonts.map((f) => f.name.toLowerCase()));
    const addedNamesLower: Set<string> = new Set<string>();
    const newFonts: FontInfo[] = [];

    files.forEach((file: File) => {
      const fontName: string = file.name.replace(/\.[^/.]+$/, '');
      const nameLower: string = fontName.toLowerCase();

      if (existingNamesLower.has(nameLower) || addedNamesLower.has(nameLower)) {
        return;
      }

      addedNamesLower.add(nameLower);
      const url: string = URL.createObjectURL(file);

      try {
        const fontFace: FontFace = new FontFace(fontName, `url(${url})`);
        fontFace
          .load()
          .then((loadedFace: FontFace) => {
            const fontSet: FontFaceSet | undefined = (document as Document & { fonts?: FontFaceSet }).fonts;
            fontSet?.add(loadedFace);
          })
          .catch((loadErr: unknown) => {
            // Avoid an unhandled promise rejection ("A network error occurred.") while keeping uploads functional.
            console.warn(`Failed to load font "${fontName}" for preview:`, loadErr);
          })
          .finally(() => {
            // Free the blob URL once the browser attempted to load it.
            URL.revokeObjectURL(url);
          });
      } catch {}

      uploadFontToAPI(file);

      newFonts.push({ name: fontName, fileName: file.name, file, url, enabled: true });
    });

    if (newFonts.length > 0) {
      const updatedFonts: FontInfo[] = [...fonts, ...newFonts];
      setFonts(updatedFonts);
      onFontListChange(updatedFonts);
    }
  };

  const handleFilesSelected = (files: File[], resetFileInput: ResetFileInput): void => {
    pendingResetRef.current = resetFileInput;
    setPendingFiles(files);
    setIsLicenseDialogOpen(true);
  };

  const handleLicenseCancel = (): void => {
    setIsLicenseDialogOpen(false);
    setPendingFiles(null);
    pendingResetRef.current?.();
    pendingResetRef.current = null;
  };

  const handleLicenseConfirm = (): void => {
    if (pendingFiles && pendingFiles.length > 0) {
      processFontFiles(pendingFiles);
    }
    setIsLicenseDialogOpen(false);
    setPendingFiles(null);
    pendingResetRef.current?.();
    pendingResetRef.current = null;
  };

  const handleDeleteFont = async (font: FontInfo, idx: number): Promise<void> => {
    const fontName: string = font.name;
    const fontFileName: string = font.fileName || (font.url.split('/').pop() ?? '');

    if (!window.confirm(t`Are you sure you want to delete the font "${fontName}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${getApiUrl()}/delete-font?projectId=${selectedProject!.id}&fontName=${encodeURIComponent(fontFileName)}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove from local state
      const updatedFonts = fonts.filter((_, i) => i !== idx);
      setFonts(updatedFonts);
      onFontListChange(updatedFonts);
      
      // Notify parent of deletion
      if (onFontDeleted) {
        onFontDeleted(fontName);
      }
    } catch (err) {
      console.error('Error deleting font:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete font');
    }
  };

  return (
    <div>
      <Dialog open={isLicenseDialogOpen} onClose={handleLicenseCancel} fullWidth maxWidth="sm">
        <DialogTitle>{t`Font License Confirmation`}</DialogTitle>
        <DialogContent>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {t`By uploading a font to the system, I confirm that I hold a valid and legal license to use this font, and that I am not infringing any copyright or terms of use. I acknowledge that full responsibility for the use of the font lies solely with me.`}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLicenseCancel}>{t`Cancel`}</Button>
          <Button onClick={handleLicenseConfirm} variant="contained">{t`Confirm`}</Button>
        </DialogActions>
      </Dialog>

      <AssetManagement<FontInfo>
        title={String(t`Font Management`)}
        uploadTitle={String(t`Upload Fonts`)}
        uploadTooltip={String(t`Supported formats: .ttf, .otf, .woff, .woff2`)}
        uploadAccept=".ttf,.otf,.woff,.woff2"
        uploadAriaLabel={String(t`Upload fonts`)}
        uploadIcon={<UploadArrowIcon width={24} height={24} style={{ display: 'block' }} aria-hidden="true" focusable="false" />}
        onFilesSelected={handleFilesSelected}
        listLabel={String(t`Font List`)}
        listTooltip={String(t`Checked fonts will be available in the Block Properties section.`)}
        searchPlaceholder={String(t`Search fonts...`)}
        emptyText={String(t`No fonts uploaded yet.`)}
        noMatchText={String(t`No fonts match your search.`)}
        loading={loading}
        loadingText={String(t`Uploading font...`)}
        error={error}
        assets={fonts}
        onAssetsChange={(next) => {
          setFonts(next);
          onFontListChange(next);
        }}
        getPrimaryStyle={(font) => ({ fontFamily: font.name })}
        isDeletable={(font) => !!font.file || isProjectOwnedAssetUrl(font.url, selectedProjectId, 'fonts')}
        deleteTitle={String(t`Delete font`)}
        onDeleteAsset={(font, idx) => {
          void handleDeleteFont(font, idx);
        }}
      />
    </div>
  );
};

export default FontManagement;
